import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { setPersistentValues } from '../util/analytics'
import { getProspectApi } from '../apis/apis'

const SLICE_NAME = 'APP_SLICE'

export const getProspect = createAsyncThunk(
  `${SLICE_NAME}/getProspect`,
  async (prospectId: string, { rejectWithValue }) => {
    try {
      try {
        let data: FeedData

        if (
          window.location.hostname !== 'registration.kingenergy.com' &&
          prospectId.includes('demo')
        ) {
          switch (prospectId) {
            case 'demo':
            case 'demo-pge':
              data = (await import('../../devData/prospectPGE')).default
              break
            case 'demo-optirate':
              data = (await import('../../devData/prospectOptiRate')).default
              break
            case 'demo-sdge':
              data = (await import('../../devData/prospectSDGE')).default
              break
            case 'demo-sce':
              data = (await import('../../devData/prospectSCE')).default
              break
            default:
              data = (await import('../../devData/prospectSDGE')).default
          }
        } else {
          const res = await getProspectApi(prospectId)
          data = res.data as FeedData
        }

        let prospect: Prospect = {
          ...data.original,
          tesaSignedAt: data.original.tesaSignedAt,
          utilityReplyToEmail: data.utilityReplyToEmail as string,
          tenant: {
            ...data.original.tenant,
            agreements: data.original.tenant.agreements,
          },
          agreements: data.original.tenant.agreements,
          agreementSubmission: data.agreementSubmission,
          source: 'ORIGINAL',
        }

        if (data.updated != null) {
          if (data.agreementSubmission?.status === 'REJECTED') {
            prospect = {
              ...data.updated,
              tesaVersion: data.original.tesaVersion,
              tesaSignedAt: data.original.tesaSignedAt,
              utilityReplyToEmail: data.utilityReplyToEmail as string,
              tenant: {
                ...data.updated.tenant,
                agreements: data.original.tenant.agreements,
                authorizeSpecificAccountsOnly: data.original.tenant.authorizeSpecificAccountsOnly,
              },
              contact: data.updated.contact,
              agreements: data.original.tenant.agreements,
              agreementSubmission: data.agreementSubmission,
              source: 'REJECTED',
            }
          } else {
            prospect = {
              ...data.updated,
              tesaVersion: data.original.tesaVersion,
              tesaSignedAt: data.original.tesaSignedAt,
              utilityReplyToEmail: data.utilityReplyToEmail as string,
              tenant: {
                ...data.updated.tenant,
                agreements: data.original.tenant.agreements,
                authorizeSpecificAccountsOnly: data.original.tenant.authorizeSpecificAccountsOnly,
              },
              agreements: data.original.tenant.agreements,
              agreementSubmission: data.agreementSubmission,
              source: 'UPDATED',
            }
          }
        }

        const originalOptiRate = (data.original as Prospect).optiRateAmendment
        const updatedOptiRate = (data.updated as Prospect | undefined)?.optiRateAmendment
        if (prospectId === 'demo-optirate') {
          if (originalOptiRate != null || updatedOptiRate != null) {
            prospect = {
              ...prospect,
              optiRateAmendment: updatedOptiRate ?? originalOptiRate,
            }
          }
        } else if (!prospectId.includes('demo')) {
          if (originalOptiRate != null || updatedOptiRate != null) {
            prospect = {
              ...prospect,
              optiRateAmendment: updatedOptiRate ?? originalOptiRate,
            }
          }
        }

        if (data.updatedProspect != null) {
          prospect = {
            ...prospect,
            updatedProspect: data.updatedProspect,
          }
        }

        setPersistentValues({
          invitationCode: prospectId,
          property: prospect.property,
          utility: prospect.utility,
          name: `${prospect.contact.firstName} ${prospect.contact.lastName}`,
          email: prospect.contact.email,
        })

        return prospect
      } catch (err: any) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (err.response?.status === 422) {
          // Nest may wrap the payload under `message`; mock/dev servers return it at the top level.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const responseBody = err.response?.data as Record<string, unknown> | undefined
          const payload =
            responseBody?.data != null
              ? responseBody
              : (responseBody?.message as Record<string, unknown> | undefined) ?? responseBody
          const consumedFeedData = payload?.data as ConsumedFeedData | undefined
          if (consumedFeedData?.contact?.firstName == null) {
            return rejectWithValue({
              err: Boolean(err),
            })
          }
          const optiRateAmendment = consumedFeedData.optiRateAmendment
          const tesaVersion = consumedFeedData.tesaVersion ?? 6
          const optiRateAccepted = optiRateAmendment?.status === 'ACCEPTED'
          return {
            version: 0,
            utility: consumedFeedData.utility,
            property: consumedFeedData.property,
            flowVersion: consumedFeedData.flowVersion,
            tesaVersion,
            tenant: {
              businessName: '',
              businessType: null,
              approximateSqft: null,
              utilityAccounts: [
                {
                  number: '',
                  billingAddress: {
                    street1: '',
                    street2: null,
                    city: '',
                    state: '',
                    zip: '',
                  },
                  serviceAgreements: [
                    {
                      meterNumber: '',
                      serviceAddress: {
                        street1: '',
                        street2: null,
                        city: '',
                        state: '',
                        zip: '',
                      },
                      tariffSchedule: '',
                      serviceAgreementId: '',
                      approximateEnergyUsage: '',
                    },
                  ],
                },
              ],
              agreements: {
                TESA: { accepted: true },
                TESA_SUPPLEMENT: { accepted: true },
                PRICING_AND_PAYMENT_TERMS: { accepted: true },
                TESA_OPTIRATE_AMENDMENT: { accepted: optiRateAccepted },
              },
            },
            contact: {
              email: '',
              phone: '',
              lastName: '',
              firstName: consumedFeedData.contact.firstName,
              title: null,
            },
            salesforceContactId: '',
            source: 'CONSUMED',
            optiRateAmendment,
          }
        } else {
          return rejectWithValue({
            err: Boolean(err),
          })
        }
      }
    } catch (err) {
      return rejectWithValue({
        err: Boolean(err),
      })
    }
  }
)

// Define a type for the slice state
interface AppState {
  prospect?: Prospect
  error?: boolean
}

// Define the initial state using that type
const initialState: AppState = {}

export const appSlice = createSlice({
  name: 'app',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setProspect: (state, action: PayloadAction<Prospect>) => {
      state.prospect = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProspect.fulfilled, (state, { payload }) => {
      state.prospect = payload
    })
    builder.addCase(getProspect.rejected, (state) => {
      state.error = true
    })
  },
})

export const { setProspect } = appSlice.actions

export default appSlice.reducer
